import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

// TODO: Replace with actual country list from API
const defaultCountryList = [
  { c_id: 82, countryName: 'India' },
  { c_id: 1, countryName: 'United States' },
  { c_id: 44, countryName: 'United Kingdom' },
];

const RegisterPage = ({ error = '', message = '', clist = defaultCountryList }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    country: '82',
    mobile: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showRegisterButton, setShowRegisterButton] = useState(true);
  const registrationEnabled = false;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const validateField = (fieldType, value) => {
    let isValid = true;
    let errorMessage = '';

    if (fieldType === 'username') {
      if (value.length < 3) {
        errorMessage = 'Username must be at least 3 characters long';
        isValid = false;
      }
    } else if (fieldType === 'email') {
      const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
      if (!emailPattern.test(value)) {
        errorMessage = 'Please enter a valid email address.';
        isValid = false;
      }
    } else if (fieldType === 'mobile') {
      if (value.length < 8 || value.length > 15) {
        errorMessage = 'Mobile number must be between 8 and 15 digits.';
        isValid = false;
      }
    } else if (fieldType === 'password') {
      if (value.length < 8) {
        errorMessage = 'Password must be at least 8 characters long.';
        isValid = false;
      }
    }

    setErrors((prev) => ({
      ...prev,
      [fieldType]: { isValid, message: errorMessage },
    }));

    const allValid = Object.values(errors).every((err) => err.isValid !== false);
    setShowRegisterButton(allValid && isValid);

    return isValid;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const logoSrc = `${process.env.PUBLIC_URL || ''}/assets/images/chat_logo.png`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        backgroundImage: `linear-gradient(135deg, rgba(37, 211, 102, 0.65) 0%, rgba(26, 160, 80, 0.7) 50%, rgba(20, 126, 63, 0.75) 100%), url(${process.env.PUBLIC_URL || ''}/assets/images/whatsapp_bg1.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 640,
          borderRadius: 4,
          boxShadow: 10,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.4)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2} alignItems="center" mb={2}>
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Button
                variant="text"
                startIcon={<ArrowLeft size={18} />}
                href="/login"
                sx={{ minWidth: 0, px: 0 }}
              >
                Back
              </Button>
            </Box>
            <Box component="img" src={logoSrc} alt="ChattyBO" sx={{ height: 40 }} />
            <Typography variant="h6">Get your free HEPTO account now.</Typography>
            {error && error.length > 0 && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
              </Alert>
            )}
            {message && message.length > 0 && (
              <Alert severity="info" sx={{ width: '100%' }}>
                {message}
              </Alert>
            )}
            {!registrationEnabled && (
              <Alert severity="info" sx={{ width: '100%' }}>
                Registration is temporarily disabled. Please check back later.
              </Alert>
            )}
          </Stack>

          {registrationEnabled && (
            <Box component="form" method="post" action="/post-register" sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <TextField
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputProps={{ minLength: 3, maxLength: 50 }}
                  required
                  fullWidth
                  error={errors.username && !errors.username.isValid}
                  helperText={errors.username && errors.username.message}
                />

                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  inputProps={{ minLength: 10 }}
                  required
                  fullWidth
                  error={errors.email && !errors.email.isValid}
                  helperText={errors.email && errors.email.message}
                />

                <FormControl fullWidth>
                  <InputLabel id="country-label">Country</InputLabel>
                  <Select
                    labelId="country-label"
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    fullWidth
                  >
                    {clist.map((country) => (
                      <MenuItem key={country.c_id} value={country.c_id}>
                        {country.countryName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Mobile"
                  name="mobile"
                  type="number"
                  value={formData.mobile}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value.length > 11) {
                      value = value.slice(0, 11);
                    }
                    setFormData({ ...formData, mobile: value });
                  }}
                  onBlur={handleBlur}
                  inputProps={{ minLength: 8, maxLength: 11 }}
                  required
                  fullWidth
                  error={errors.mobile && !errors.mobile.isValid}
                  helperText={errors.mobile && errors.mobile.message}
                />

                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value.length > 20) {
                      value = value.slice(0, 20);
                    }
                    setFormData({ ...formData, password: value });
                  }}
                  onBlur={handleBlur}
                  inputProps={{ minLength: 8, maxLength: 20 }}
                  required
                  fullWidth
                  error={errors.password && !errors.password.isValid}
                  helperText={errors.password && errors.password.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePassword} edge="end" aria-label="toggle password visibility">
                          {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <input type="hidden" id="role" name="role" value="FREE" />

                <Stack alignItems="center" spacing={1} mt={1}>
                  <Button
                    id="registerButton"
                    type="submit"
                    variant="contained"
                    sx={{ width: { xs: '100%', sm: '50%' } }}
                    disabled={!showRegisterButton}
                  >
                    Register
                  </Button>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    By registering you agree to the HEPTO{' '}
                    <Link href="javascript:void(0)" underline="hover" sx={{ color: '#2ab63f' }}>
                      Terms of Use
                    </Link>
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          <Stack alignItems="center" mt={3}>
            <Typography variant="body2">
              Already have an account?{' '}
              <Link href="/login" underline="hover" sx={{ color: '#2ab63f' }}>
                Login
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RegisterPage;
